/**
 * MasterDataTypeOfInspectionFormDialog — Create/Edit `type_of_inspection`.
 * 1:1 port of Access `frmMdQryTypeOfInspectionAdd`. Compact Sales Contract style.
 *
 * Single-column (Access all inputs Left=3108), field order by Top:
 *   Inspection Code → Inspection Type → Note.
 * `inspection_code` là PK auto-suggest (Access `Suggest_IndexName("type_of_inspection")`
 * gọi ở btnNew của frmMdMainModItemsValuation) — áp /access-suggest:
 *   - New: auto-suggest khi mở + blur-check trùng → tự đổi mã + alert vàng.
 *   - Edit: disable, không suggest/check.
 * VBA errCheck: code trống → "Please enter valid Type Of Inspection Code!";
 *   type trống → "Please type Inspection Type!"; code trùng (create) →
 *   "Type Of Inspection Code existed! Please enter a new Code!" (BE raise).
 */
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCreateMdTypeOfInspectionMutation,
  useLazyCheckMdInspectionCodeQuery,
  useLazySuggestMdInspectionCodeQuery,
  useUpdateMdTypeOfInspectionMutation,
} from '@/store/api/masterDataCrudApi'
import type { MasterDataTypeOfInspection } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import { compactDialogContentSx, dialogHeaderBtnSx, dialogTitleActionsSx, dialogTitleSx, dialogTitleTextSx, roSx } from '../purchase/dialogStyle'

export interface Props {
  open: boolean
  mode: 'create' | 'edit'
  initialValue?: MasterDataTypeOfInspection | null
  onClose: () => void
  onSaved?: () => void
}

type FormState = {
  inspection_code: string
  inspection_type: string
  note: string
}

const EMPTY: FormState = { inspection_code: '', inspection_type: '', note: '' }

export default function MasterDataTypeOfInspectionFormDialog({
  open,
  mode,
  initialValue,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [createInspection, { isLoading: creating }] = useCreateMdTypeOfInspectionMutation()
  const [updateInspection, { isLoading: updating }] = useUpdateMdTypeOfInspectionMutation()
  const [triggerSuggest, suggestState] = useLazySuggestMdInspectionCodeQuery()
  const [triggerCheck] = useLazyCheckMdInspectionCodeQuery()
  const loading = creating || updating

  const [form, setForm] = useState<FormState>(EMPTY)
  const [showErrors, setShowErrors] = useState(false)
  const [regenNotice, setRegenNotice] = useState<{ from: string; to: string } | null>(null)
  const [precheckBusy, setPrecheckBusy] = useState(false)
  const precheckBusyRef = useRef(false)

  // Hydrate — chỉ depend [open, mode, initialValue] (access-suggest anti #4).
  useEffect(() => {
    if (!open) return
    setShowErrors(false)
    setRegenNotice(null)
    setPrecheckBusy(false)
    precheckBusyRef.current = false
    if (isEdit && initialValue) {
      setForm({
        inspection_code: initialValue.inspection_code ?? '',
        inspection_type: initialValue.inspection_type ?? '',
        note: initialValue.note ?? '',
      })
    } else {
      setForm(EMPTY)
    }
  }, [open, isEdit, initialValue])

  // New: auto-suggest inspection_code khi mở (lazy, guard chỉ set khi trống).
  useEffect(() => {
    if (!open || isEdit) return
    let cancelled = false
    void triggerSuggest(undefined, false).unwrap().then((res) => {
      if (cancelled) return
      const newVal = (res.inspection_code || '').trim()
      if (!newVal) return
      setForm((prev) => (prev.inspection_code.trim() ? prev : { ...prev, inspection_code: newVal }))
    }).catch(() => {})
    return () => { cancelled = true }
  }, [open, isEdit, triggerSuggest])

  // Dismiss alert khi user sửa code khác mã suggest.
  useEffect(() => {
    if (regenNotice && form.inspection_code !== regenNotice.to) setRegenNotice(null)
  }, [form.inspection_code, regenNotice])

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }))

  const codeEmpty = !form.inspection_code.trim()
  const typeEmpty = !form.inspection_type.trim()
  const canSave = !codeEmpty && !typeEmpty && !loading

  // Blur field PK → check trùng, nếu có thì suggest mã mới (access-suggest).
  const handleCodeBlur = async () => {
    if (isEdit) return
    const val = form.inspection_code.trim()
    if (!val) return
    precheckBusyRef.current = true
    setPrecheckBusy(true)
    try {
      const check = await triggerCheck(val, false).unwrap()
      if (!check.exists) return
      const next = await triggerSuggest(undefined, false).unwrap()
      const newVal = (next.inspection_code || '').trim()
      if (newVal && newVal !== val) {
        setForm((prev) => ({ ...prev, inspection_code: newVal }))
        setRegenNotice({ from: val, to: newVal })
      }
    } catch {
      /* BE raise lúc Save nếu thật sự trùng — không poison UX */
    } finally {
      precheckBusyRef.current = false
      setPrecheckBusy(false)
    }
  }

  const handleClose = () => {
    if (loading) return
    onClose()
  }

  const handleSave = async () => {
    if (precheckBusyRef.current) return // race guard (blur + click Save cùng tick)
    if (codeEmpty || typeEmpty) {
      setShowErrors(true)
      return
    }
    const body = {
      inspection_type: form.inspection_type.trim(),
      note: form.note.trim() || null,
    }
    try {
      if (isEdit && initialValue) {
        await updateInspection({ code: initialValue.inspection_code, body }).unwrap()
        snack.success('Type of inspection updated')
      } else {
        await createInspection({ inspection_code: form.inspection_code.trim(), ...body }).unwrap()
        snack.success('Type of inspection created')
      }
      onSaved?.()
      onClose()
    } catch (e) {
      // access-suggest anti #13: duplicate race → auto re-suggest + alert vàng.
      const msg = getErrorMessage(e, isEdit ? 'Failed to update' : 'Failed to create')
      const lower = msg.toLowerCase()
      if (!isEdit && (lower.includes('exist') || lower.includes('duplicate'))) {
        try {
          const next = await triggerSuggest(undefined, false).unwrap()
          const newVal = (next.inspection_code || '').trim()
          const oldVal = form.inspection_code.trim()
          if (newVal && newVal !== oldVal) {
            setForm((prev) => ({ ...prev, inspection_code: newVal }))
            setRegenNotice({ from: oldVal, to: newVal })
            return
          }
        } catch {
          /* fallthrough → snack error */
        }
      }
      snack.error(msg)
    }
  }

  const codeHelper = isEdit
    ? 'Inspection Code cannot be changed after creation.'
    : precheckBusy
      ? 'Verifying code…'
      : suggestState.isFetching
        ? 'Suggesting next code…'
        : 'Suggested code below; edit if needed.'

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (loading) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" noWrap sx={dialogTitleTextSx}>
          {isEdit ? 'Edit Type of Inspection' : 'New Type of Inspection'}
          {isEdit && initialValue ? (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              {initialValue.inspection_code}
            </Typography>
          ) : null}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={!canSave}
            sx={dialogHeaderBtnSx}
          >
            {loading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" size="small" onClick={handleClose} disabled={loading} sx={dialogHeaderBtnSx}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        {regenNotice ? (
          <Alert severity="warning" sx={{ mb: 1.25, py: 0.25 }}>
            Inspection Code <b>{regenNotice.from}</b> was just created — suggested new code{' '}
            <b>{regenNotice.to}</b>.
          </Alert>
        ) : null}
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <TextField
              label="Inspection Code"
              size="small"
              fullWidth
              required
              disabled={isEdit}
              error={showErrors && codeEmpty}
              value={form.inspection_code}
              onChange={(e) => set('inspection_code', e.target.value)}
              onBlur={() => void handleCodeBlur()}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: isEdit ? -1 : 1 }}
              helperText={codeHelper}
              sx={isEdit ? roSx : undefined}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Inspection Type"
              size="small"
              fullWidth
              required
              error={showErrors && typeEmpty}
              value={form.inspection_type}
              onChange={(e) => set('inspection_type', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 2 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Note"
              size="small"
              fullWidth
              multiline
              minRows={3}
              value={form.note}
              onChange={(e) => set('note', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 3 }}
            />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
